import React, { useState, useRef, useEffect } from 'react';
import './index.css';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DoubleArrow from '@mui/icons-material/DoubleArrow';
// @ts-ignore
import { v4 as uuidv4 } from 'uuid';

import axios from 'axios';


interface Slide {
    id: number;
    title: string;
    description: string;
    fileName: string;
    presentation: {
        id: number;
        title: string;
        description: string;
        status: string;
        slidesRoot: string;
        createdAt: string;
    };
    durationMs: number;
    picture: {
        id: number;
        fileName: string;
        raw: string;
    };
    status: string;
    createdAt: string;
}

interface Visit {
    id: number;
    title: string;
    description: string;
    picture: any;
    slides: Slide[];
    video: {
        raw: string;
        hls: string | null;
        status: string;
    };
    createdAt: string;
}

interface Project {
    id: number;
    title: string;
}

interface VideoPlayerProps {
    videoUrl: string;
    slides: Slide[];
    visit: Visit;
    project: Project;
    doctorId: string;
}

interface SlideWithTime extends Slide {
    start: number;
    end: number;
}


const VideoPlayer: React.FC<VideoPlayerProps> = ({ videoUrl, slides, visit, project, doctorId }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [currentSlide, setCurrentSlide] = useState<SlideWithTime | null>(null);
    const [isMuted, setIsMuted] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [fadeOut, setFadeOut] = useState(false);
    const controlsTimeout = useRef<NodeJS.Timeout | null>(null);
    const [isSeeking, setIsSeeking] = useState(false);
    const progressBarRef = useRef<HTMLDivElement>(null);

    // Для рейтинга
    const [showRating, setShowRating] = useState(false);
    const [selectedRating, setSelectedRating] = useState<number | null>(null);
    const [ratingSubmitted, setRatingSubmitted] = useState(false);

    // Генерация sessionId
    const sessionIdRef = useRef<string>(uuidv4());

    // Формируем slides с временными метками
    const slidesWithTime: SlideWithTime[] = React.useMemo(() => {
        let currentStart = 0;
        return slides.map(slide => {
            const s: SlideWithTime = {
                ...slide,
                start: currentStart,
                end: currentStart + slide.durationMs,
            };
            currentStart += slide.durationMs;
            return s;
        });
    }, [slides]);

    // Сбросить таймер и запустить плавное скрытие через 5 сек.
    const handleUserActivity = () => {
        setShowControls(true);
        setFadeOut(false);
        if (controlsTimeout.current) clearTimeout(controlsTimeout.current);
        controlsTimeout.current = setTimeout(() => setFadeOut(true), 5000);
    };

    // Клик по пустой области: если панель видна — скрыть сразу, если скрыта — показать сразу
    const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            if (showControls) {
                setShowControls(false);
                setFadeOut(false);
                if (controlsTimeout.current) clearTimeout(controlsTimeout.current);
            } else {
                setShowControls(true);
                setFadeOut(false);
                handleUserActivity();
            }
        }
    };

    useEffect(() => {
        handleUserActivity();
        return () => {
            if (controlsTimeout.current) clearTimeout(controlsTimeout.current);
        };
    }, []);

    // Обновление текущего слайда
    useEffect(() => {
        if (slidesWithTime.length === 0) return;
        const slide = slidesWithTime.find(s => currentTime >= s.start && currentTime < s.end);
        setCurrentSlide(slide || null);
    }, [currentTime, slidesWithTime]);

    // Отправка статистики каждую секунду
    useEffect(() => {
        const interval = setInterval(() => {
            if (
                !videoRef.current ||
                videoRef.current.currentTime === 0 ||
                videoRef.current.ended ||
                !isPlaying
            ) {
                return;
            }
            const ms = Math.floor(videoRef.current.currentTime * 1000);
            const slide = slidesWithTime.find(s => ms >= s.start && ms < s.end);
            if (!slide) return;

            const stat = {
                doctor_id: doctorId,
                project: project.title,
                project_id: project.id+"",
                slide: slide.title,
                slide_id: slide.id+"",
                device: "Mobile",
                session_id: sessionIdRef.current,
                presentation_duration: Math.ceil(videoRef.current.duration),
                slide_duration: Math.ceil(slide.durationMs / 1000)
            }

            // Здесь отправка статистики (замените на ваш endpoint)
            fetch('https://stat.selfdetailing.ru/statistics/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(stat),
            });

            // Для отладки:
            console.log('STAT', stat);
        }, 1000);

        return () => clearInterval(interval);
    }, [isPlaying, slidesWithTime, doctorId, visit]);

    // Обновление текущего времени видео
    const handleTimeUpdate = () => {
        if (videoRef.current) {
            setCurrentTime(Math.floor(videoRef.current.currentTime * 1000));
        }
    };


    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const getTimeFromPosition = (clientX: number) => {
        if (!progressBarRef.current) return 0;
        const rect = progressBarRef.current.getBoundingClientRect();
        let x = clientX - rect.left;
        x = Math.max(0, Math.min(x, rect.width));
        const percent = x / rect.width;
        const duration = slidesWithTime.length > 0 ? slidesWithTime[slidesWithTime.length - 1].end : 0;
        return percent * duration;
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsSeeking(true);
        const newTime = getTimeFromPosition(e.clientX);
        if (videoRef.current) {
            videoRef.current.currentTime = newTime / 1000;
            setCurrentTime(newTime);
        }
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isSeeking) return;
        const newTime = getTimeFromPosition(e.clientX);
        if (videoRef.current) {
            videoRef.current.currentTime = newTime / 1000;
            setCurrentTime(newTime);
        }
    };

    const handleMouseUp = () => {
        setIsSeeking(false);
    };

    useEffect(() => {
        if (isSeeking) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isSeeking]);

    const handleTouchStart = (e: React.TouchEvent) => {
        setIsSeeking(true);
        const touch = e.touches[0];
        const newTime = getTimeFromPosition(touch.clientX);
        if (videoRef.current) {
            videoRef.current.currentTime = newTime / 1000;
            setCurrentTime(newTime);
        }
    };

    const handleTouchMove = (e: TouchEvent) => {
        if (!isSeeking) return;
        const touch = e.touches[0];
        const newTime = getTimeFromPosition(touch.clientX);
        if (videoRef.current) {
            videoRef.current.currentTime = newTime / 1000;
            setCurrentTime(newTime);
        }
    };

    const handleTouchEnd = () => {
        setIsSeeking(false);
    };

    useEffect(() => {
        if (isSeeking) {
            window.addEventListener('touchmove', handleTouchMove);
            window.addEventListener('touchend', handleTouchEnd);
        } else {
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
        }
        return () => {
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isSeeking]);

    const totalDuration = slidesWithTime.length > 0 ? slidesWithTime[slidesWithTime.length - 1].end : 0;


    // Хук отслеживания окончания видео
    useEffect(() => {
        if (!videoRef.current) return;
        const handleEnded = () => setShowRating(true);
        const videoEl = videoRef.current;
        videoEl.addEventListener('ended', handleEnded);
        return () => videoEl.removeEventListener('ended', handleEnded);
    }, []);

    // Обработка выбора звезды
    const handleRate = async (value: number) => {
        setSelectedRating(value);
        setRatingSubmitted(true);
        // POST-запрос
        await fetch('https://stat.selfdetailing.ru/ratings/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                session_id: sessionIdRef.current,
                doctor_id: doctorId,
                project_id: project.id + "",
                value
            }),
        });
    };


    return (
        <div
            id='videoplayer'
            className="video-container"
            onMouseMove={handleUserActivity}
            onMouseDown={handleUserActivity}
            onTouchStart={handleUserActivity}
            onClick={handleContainerClick}
        >
            <video
                ref={videoRef}
                poster={'https://player.selfdetailing.ru/1.png'}
                src={videoUrl}
                controls={false}
                onTimeUpdate={handleTimeUpdate}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
            <div className={`controls-overlay${(!showControls || fadeOut) ? ' hide' : ''}`}>
                <>
                    {/* Назад на 10 секунд */}
                    <button
                        className="seek-btn prev-btn"
                        aria-label="Назад на 10 секунд"
                        onClick={() => {
                            if (videoRef.current) {
                                videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10);
                            }
                        }}
                    >
                        <DoubleArrow fontSize="inherit"/>
                    </button>
                    {/* Play/Pause */}
                    <button
                        className="big-play-btn"
                        onClick={togglePlay}
                        aria-label={isPlaying ? "Pause video" : "Play video"}
                    >
                        {isPlaying ? <PauseIcon fontSize="inherit" /> : <PlayArrowIcon fontSize="inherit" />}
                    </button>
                    {/* Вперёд на 10 секунд */}
                    <button
                        className="seek-btn"
                        aria-label="Вперёд на 10 секунд"
                        onClick={() => {
                            if (videoRef.current) {
                                videoRef.current.currentTime = Math.min(
                                    videoRef.current.duration,
                                    videoRef.current.currentTime + 10
                                );
                            }
                        }}
                    >
                        <DoubleArrow fontSize="inherit"/>
                    </button>
                    <div
                        className="progress-bar-container"
                        ref={progressBarRef}
                        onMouseDown={handleMouseDown}
                        onTouchStart={handleTouchStart}
                    >
                        <div
                            className="progress-bar-fill"
                            style={{ width: `${(currentTime / totalDuration) * 100}%` }}
                        />
                    </div>


                </>
            </div>
            {/* Блок рейтинга поверх видео после завершения */}
            {showRating && (
                <div className="rating-overlay">
                    {!ratingSubmitted ? (
                        <>
                            <div>
                                <div>Оцените видео:</div>
                                {/*<div>*/}
                                {/*    {[1,2,3,4,5].map(star => (*/}
                                {/*        <span*/}
                                {/*            key={star}*/}
                                {/*            onClick={() => handleRate(star)}*/}
                                {/*            style={{*/}
                                {/*                fontSize: '2em',*/}
                                {/*                cursor: 'pointer',*/}
                                {/*                color: selectedRating && star <= selectedRating ? '#ffc107' : '#ddd'*/}
                                {/*            }}*/}
                                {/*        >★</span>*/}
                                {/*    ))}*/}
                                {/*</div>*/}

                                <div style={{display: 'flex', gap: '1vw', marginTop: '1vw'}}>
                                    {[1,2,3,4,5].map(star => (
                                        <span
                                            key={star}
                                            onClick={() => setSelectedRating(star)}
                                            style={{
                                                fontSize: '2em',
                                                cursor: 'pointer',
                                                color: selectedRating && star <= selectedRating ? '#ffc107' : '#ddd'
                                            }}
                                        >★</span>
                                    ))}
                                </div>
                                <button
                                    onClick={() => handleRate(selectedRating!)}
                                    disabled={!selectedRating}
                                    style={{
                                        marginTop: 24,
                                        padding: "0.5em 1.2em",
                                        fontSize: "1em",
                                        border: 0,
                                        borderRadius: '0.3em',
                                        cursor: selectedRating ? 'pointer' : 'not-allowed',
                                        opacity: selectedRating ? 1 : 0.4
                                    }}
                                >
                                    Оценить
                                </button>
                            </div>

                        </>
                    ) : (
                        <div style={{fontSize: '1.5em'}}>Спасибо за оценку!</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default VideoPlayer;
